/**
 * Campaigns view — F5→message.
 *
 * Upgraded from basic name/objective form to the envoy 6-step campaign wizard:
 *   Name → Content → Inboxes → Leads → Window → Review & Activate
 *
 * Each step has per-step validation. The readiness gate blocks activation
 * until prerequisites are met. Daily capacity is computed from eligible inboxes.
 *
 * Lineage: envoy portal campaign-setup-dialog.tsx, adapted for the unified
 * portal's Allen & Co aesthetic and MembraneLanguage.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { getClients, applyMembrane } from "../lib/api-clients";

type Step = "name" | "content" | "inboxes" | "leads" | "window" | "review";

const STEPS: { id: Step; label: string }[] = [
  { id: "name", label: "Name" },
  { id: "content", label: "Message" },
  { id: "inboxes", label: "Inboxes" },
  { id: "leads", label: "Leads" },
  { id: "window", label: "Send window" },
  { id: "review", label: "Review" },
];

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"] as const;

export function CampaignsView({ isOperator }: { isOperator: boolean }) {
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [wizardError, setWizardError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  // Wizard state
  const [step, setStep] = useState<Step>("name");
  const [name, setName] = useState("");
  const [sequences, setSequences] = useState<any[]>([]);
  const [sequenceId, setSequenceId] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [useCustomContent, setUseCustomContent] = useState(false);

  const [inboxes, setInboxes] = useState<any[]>([]);
  const [eligibleInboxIds, setEligibleInboxIds] = useState<Set<string>>(new Set());

  const [contacts, setContacts] = useState<any[]>([]);
  const [enrolledContactIds, setEnrolledContactIds] = useState<Set<string>>(new Set());
  const [contactSearch, setContactSearch] = useState("");

  const [windowStart, setWindowStart] = useState("09:00");
  const [windowEnd, setWindowEnd] = useState("17:00");
  const [timezone, setTimezone] = useState("America/New_York");
  const [sendingDays, setSendingDays] = useState<string[]>(["MON", "TUE", "WED", "THU", "FRI"]);
  const [maxLeadsPerDay, setMaxLeadsPerDay] = useState(50);
  const [rotation, setRotation] = useState("ROUND_ROBIN");
  const [minGap, setMinGap] = useState(3);

  const membraneBypass = isOperator;

  // Load campaigns list
  const loadCampaigns = useCallback(async () => {
    const clients = getClients();
    if (!clients) { setLoading(false); return; }
    try {
      const res: any = await clients.envoy.listCampaigns();
      setCampaigns(applyMembrane(res?.campaigns || res || [], membraneBypass));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [membraneBypass]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  // Load wizard data when opened
  useEffect(() => {
    if (!showWizard) return;
    const clients = getClients();
    if (!clients) return;

    (async () => {
      try {
        const [seqs, inb, conts] = await Promise.all([
          clients.envoy.listSequences().catch(() => ({ sequences: [] })),
          clients.envoy.getAgentContext().catch(() => ({})),
          clients.envoy.listContacts({ limit: 100 }).catch(() => ({ contacts: [] })),
        ]);
        const seqList = ((seqs as any)?.sequences || []).filter((x: any) => x.status !== "ARCHIVED");
        setSequences(seqList);
        if (seqList.length > 0 && !sequenceId) setSequenceId(seqList[0].id);

        const ctxData = inb as any;
        const inboxList = (ctxData?.inboxes || []).filter((x: any) =>
          ((x.inbox_type || "").toUpperCase() === "COLD_EMAIL") &&
          ((x.warmup_stage || "").toUpperCase() !== "PAUSED")
        );
        setInboxes(inboxList);

        setContacts((conts as any)?.contacts || []);
      } catch (err: any) {
        setWizardError(err?.message || "Could not load campaign data.");
      }
    })();
  }, [showWizard]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset wizard when opened
  useEffect(() => {
    if (showWizard) {
      setStep("name");
      setName("");
      setSequenceId("");
      setCustomSubject("");
      setCustomBody("");
      setUseCustomContent(false);
      setEligibleInboxIds(new Set());
      setEnrolledContactIds(new Set());
      setSubmitting(false);
      setWizardError(null);
      setCreatedId(null);
    }
  }, [showWizard]);

  const eligibleInboxes = useMemo(
    () => inboxes.filter((i) => eligibleInboxIds.has(i.id)),
    [inboxes, eligibleInboxIds]
  );

  const dailyCapacity = useMemo(() =>
    eligibleInboxes.reduce((sum, i) => sum + Number(i.daily_send_limit || 0), 0),
    [eligibleInboxes]
  );

  const totalEnrolled = enrolledContactIds.size;
  const daysAtCapacity = dailyCapacity > 0 ? Math.ceil(totalEnrolled / dailyCapacity) : 0;

  const filteredContacts = useMemo(() => {
    if (!contactSearch) return contacts;
    const q = contactSearch.toLowerCase();
    return contacts.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.company || "").toLowerCase().includes(q)
    );
  }, [contacts, contactSearch]);

  // Per-step validation
  const validation = useMemo(() => ({
    name: name.trim().length > 0,
    content: !!sequenceId || (useCustomContent && customSubject.trim().length > 0 && customBody.trim().length > 0),
    inboxes: eligibleInboxIds.size > 0,
    leads: enrolledContactIds.size > 0,
    window: /^\d{2}:\d{2}$/.test(windowStart) && /^\d{2}:\d{2}$/.test(windowEnd) && sendingDays.length > 0,
  }), [name, sequenceId, useCustomContent, customSubject, customBody, eligibleInboxIds, enrolledContactIds, windowStart, windowEnd, sendingDays]);

  const stepOrder: Step[] = ["name", "content", "inboxes", "leads", "window", "review"];
  const currentIdx = stepOrder.indexOf(step);
  const canAdvance = validation[step as keyof typeof validation] ?? true;

  const goNext = () => {
    const next = stepOrder[currentIdx + 1];
    if (next) setStep(next);
  };
  const goBack = () => {
    const prev = stepOrder[currentIdx - 1];
    if (prev) setStep(prev);
  };

  const handleCreate = async (): Promise<string | null> => {
    const clients = getClients();
    if (!clients) return null;
    setSubmitting(true);
    setWizardError(null);
    try {
      const payload: any = {
        name: name.trim(),
        sending_days: sendingDays,
        sending_window_start: windowStart,
        sending_window_end: windowEnd,
        timezone,
        min_gap_min: minGap,
        max_new_leads_per_day: maxLeadsPerDay,
        rotation_strategy: rotation,
      };
      if (sequenceId) payload.sequence_id = sequenceId;
      if (useCustomContent) {
        payload.custom_subject = customSubject;
        payload.custom_body = customBody;
      }

      const created: any = await clients.envoy.createCampaign(payload);
      const campaignId = created.id || created.campaign?.id;
      if (!campaignId) throw new Error("No campaign id returned");

      // Assign inboxes
      if (eligibleInboxIds.size > 0) {
        await clients.envoy.enrollContacts(campaignId, [...eligibleInboxIds] as any);
      }

      // Enroll leads
      if (enrolledContactIds.size > 0) {
        await clients.envoy.enrollContacts(campaignId, [...enrolledContactIds]);
      }

      setCreatedId(campaignId);
      return campaignId;
    } catch (err: any) {
      setWizardError(err?.message || "Failed to create campaign.");
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivate = async () => {
    const id = createdId || await handleCreate();
    if (!id) return;
    const clients = getClients();
    if (!clients) return;
    try {
      // Activate the campaign
      await clients.envoy.createCampaign({ name, activate: true } as any);
      setShowWizard(false);
      await loadCampaigns();
    } catch (err: any) {
      setWizardError(err?.message || "Failed to activate.");
    }
  };

  const toggleDay = (day: string) => {
    setSendingDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const toggleInbox = (id: string) => {
    setEligibleInboxIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleContact = (id: string) => {
    setEnrolledContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) return <div className="loading">{applyMembrane("Loading campaigns…", membraneBypass)}</div>;

  return (
    <div className="view">
      <div className="view-header">
        <span className="eyebrow">{applyMembrane("Outreach", membraneBypass)}</span>
        <h1>{applyMembrane("Campaigns", membraneBypass)}</h1>
        <button className="btn-primary" onClick={() => setShowWizard(!showWizard)}>
          {showWizard ? "Cancel" : applyMembrane("New campaign", membraneBypass)}
        </button>
      </div>

      {error && <div className="error-state">{error}</div>}

      {/* ── 6-Step Campaign Wizard ─────────────────────────────────────────── */}
      {showWizard && (
        <div className="card wizard-card">
          {/* Step indicator */}
          <div className="wizard-steps">
            {STEPS.map((s, i) => (
              <div
                key={s.id}
                className={`wizard-step ${step === s.id ? "active" : ""} ${i < currentIdx ? "done" : ""}`}
              >
                <span className="wizard-step-num">{i < currentIdx ? "✓" : i + 1}</span>
                <span className="wizard-step-label">{applyMembrane(s.label, membraneBypass)}</span>
              </div>
            ))}
          </div>

          {wizardError && <div className="error-state">{wizardError}</div>}

          {/* Step: Name */}
          {step === "name" && (
            <div className="wizard-body">
              <h3>Name your campaign</h3>
              <p className="muted">Give it a clear name you'll recognize in the register.</p>
              <input
                className="text-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Q3 outbound — Series A startups"
                autoFocus
              />
            </div>
          )}

          {/* Step: Content */}
          {step === "content" && (
            <div className="wizard-body">
              <h3>Message content</h3>
              <p className="muted">Choose a sequence or write custom content.</p>
              <div className="wizard-choice">
                <label className="wizard-radio">
                  <input type="radio" checked={!useCustomContent} onChange={() => setUseCustomContent(false)} />
                  <span>Use existing sequence</span>
                </label>
                {!useCustomContent && (
                  <select className="text-input" value={sequenceId} onChange={(e) => setSequenceId(e.target.value)}>
                    <option value="">Select a sequence…</option>
                    {sequences.map((s) => (
                      <option key={s.id} value={s.id}>{s.name} ({s.step_count ?? 0} steps)</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="wizard-choice">
                <label className="wizard-radio">
                  <input type="radio" checked={useCustomContent} onChange={() => setUseCustomContent(true)} />
                  <span>Write custom content</span>
                </label>
                {useCustomContent && (
                  <>
                    <input className="text-input" placeholder="Subject line" value={customSubject} onChange={(e) => setCustomSubject(e.target.value)} />
                    <textarea className="text-input" placeholder="Email body…" value={customBody} onChange={(e) => setCustomBody(e.target.value)} rows={6} />
                  </>
                )}
              </div>
              {sequences.length === 0 && !useCustomContent && (
                <p className="muted">No sequences found. Create one first, or use custom content.</p>
              )}
            </div>
          )}

          {/* Step: Inboxes */}
          {step === "inboxes" && (
            <div className="wizard-body">
              <h3>Eligible inboxes</h3>
              <p className="muted">
                Select warmed-up cold email inboxes. Daily capacity: <strong>{dailyCapacity}</strong> sends/day.
              </p>
              {inboxes.length === 0 ? (
                <div className="empty-state">No warmed-up cold email inboxes found. Configure inboxes in Integrations first.</div>
              ) : (
                <div className="inbox-list">
                  {inboxes.map((inb) => (
                    <label key={inb.id} className={`wizard-check-row ${eligibleInboxIds.has(inb.id) ? "selected" : ""}`}>
                      <input type="checkbox" checked={eligibleInboxIds.has(inb.id)} onChange={() => toggleInbox(inb.id)} />
                      <div className="wizard-check-info">
                        <span className="wizard-check-label">{inb.email || inb.id}</span>
                        <span className="muted">{inb.daily_send_limit || 0}/day · {inb.warmup_stage || "unknown"}</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step: Leads */}
          {step === "leads" && (
            <div className="wizard-body">
              <h3>Enroll leads</h3>
              <p className="muted">
                {totalEnrolled} enrolled · {dailyCapacity > 0 ? `${daysAtCapacity} days at capacity` : "select inboxes first"}
              </p>
              <input
                className="text-input search-input"
                placeholder="Search contacts…"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
              />
              {contacts.length === 0 ? (
                <div className="empty-state">No contacts found. Add contacts first.</div>
              ) : (
                <div className="contact-select-list">
                  {filteredContacts.slice(0, 50).map((c) => (
                    <label key={c.id} className={`wizard-check-row ${enrolledContactIds.has(c.id) ? "selected" : ""}`}>
                      <input type="checkbox" checked={enrolledContactIds.has(c.id)} onChange={() => toggleContact(c.id)} />
                      <div className="wizard-check-info">
                        <span className="wizard-check-label">{c.name || c.email}</span>
                        <span className="muted">{c.email} · {c.company || "—"}</span>
                      </div>
                    </label>
                  ))}
                  {filteredContacts.length > 50 && (
                    <p className="muted">Showing 50 of {filteredContacts.length}. Refine search to see more.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step: Window */}
          {step === "window" && (
            <div className="wizard-body">
              <h3>Send window</h3>
              <p className="muted">Define when your agent sends. Respects timezone and sending days.</p>
              <div className="form-row">
                <label>Timezone</label>
                <select className="text-input" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                  <option value="America/New_York">Eastern</option>
                  <option value="America/Chicago">Central</option>
                  <option value="America/Denver">Mountain</option>
                  <option value="America/Los_Angeles">Pacific</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
              <div className="wizard-row">
                <div className="form-row">
                  <label>Window start</label>
                  <input className="text-input" type="time" value={windowStart} onChange={(e) => setWindowStart(e.target.value)} />
                </div>
                <div className="form-row">
                  <label>Window end</label>
                  <input className="text-input" type="time" value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <label>Sending days</label>
                <div className="day-toggles">
                  {DAYS.map((day) => (
                    <button
                      key={day}
                      className={`day-toggle ${sendingDays.includes(day) ? "active" : ""}`}
                      onClick={() => toggleDay(day)}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
              <div className="wizard-row">
                <div className="form-row">
                  <label>Max new leads/day</label>
                  <input className="text-input" type="number" value={maxLeadsPerDay} onChange={(e) => setMaxLeadsPerDay(Number(e.target.value))} min={1} />
                </div>
                <div className="form-row">
                  <label>Min gap (minutes)</label>
                  <input className="text-input" type="number" value={minGap} onChange={(e) => setMinGap(Number(e.target.value))} min={1} />
                </div>
              </div>
              <div className="form-row">
                <label>Rotation strategy</label>
                <select className="text-input" value={rotation} onChange={(e) => setRotation(e.target.value)}>
                  <option value="ROUND_ROBIN">Round robin</option>
                  <option value="RANDOM">Random</option>
                  <option value="LEAST_SENT">Least sent</option>
                </select>
              </div>
            </div>
          )}

          {/* Step: Review */}
          {step === "review" && (
            <div className="wizard-body">
              <h3>Review & activate</h3>
              <div className="review-grid">
                <div className="review-item">
                  <span className="review-label">Name</span>
                  <span className="review-value">{name}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Content</span>
                  <span className="review-value">
                    {useCustomContent ? `Custom: "${customSubject}"` : sequences.find((s) => s.id === sequenceId)?.name || "—"}
                  </span>
                </div>
                <div className="review-item">
                  <span className="review-label">Inboxes</span>
                  <span className="review-value">{eligibleInboxIds.size} selected · {dailyCapacity}/day capacity</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Leads</span>
                  <span className="review-value">{totalEnrolled} enrolled · {daysAtCapacity} days at capacity</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Window</span>
                  <span className="review-value">{windowStart}–{windowEnd} {timezone} · {sendingDays.join(", ")}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Limits</span>
                  <span className="review-value">{maxLeadsPerDay}/day · {minGap}min gap · {rotation}</span>
                </div>
              </div>
              {createdId && (
                <div className="card" style={{ background: "var(--green-soft)", borderColor: "var(--green)" }}>
                  <div className="card-title">Campaign created: {createdId.slice(0, 12)}…</div>
                  <p className="muted">Click activate to start sending.</p>
                </div>
              )}
            </div>
          )}

          {/* Wizard navigation */}
          <div className="wizard-nav">
            <button className="btn-secondary" onClick={goBack} disabled={currentIdx === 0 || submitting}>
              Back
            </button>
            {step !== "review" ? (
              <button className="btn-primary" onClick={goNext} disabled={!canAdvance}>
                Next
              </button>
            ) : !createdId ? (
              <button className="btn-primary" onClick={handleCreate} disabled={submitting}>
                {submitting ? "Creating…" : "Create campaign"}
              </button>
            ) : (
              <button className="btn-primary" onClick={handleActivate} disabled={submitting}>
                {submitting ? "Activating…" : "Activate campaign"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Campaigns list ────────────────────────────────────────────────── */}
      {!showWizard && (
        campaigns.length === 0 ? (
          <div className="empty-state">
            {applyMembrane("No campaigns yet. Create your first outreach campaign.", membraneBypass)}
          </div>
        ) : (
          <div className="card-grid">
            {campaigns.map((c: any, i: number) => (
              <div key={i} className="card">
                <div className="card-title">{applyMembrane(c.name, membraneBypass)}</div>
                <div className="card-meta">
                  <span className="badge" data-status={c.status || "pending"}>{c.status || "draft"}</span>
                  <span>{c.leads_count ?? 0} contacts</span>
                  <span>{c.replies ?? 0} replies</span>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
